import { Suspense } from 'react';
import { Outlet } from 'react-router-dom';
import { Check, Loader2 } from 'lucide-react';
import { BrandLockup } from '@/components/brand/BrandMark';
import { ErrorBoundary } from '@/components/ui/error-boundary';
import { COMPANY_NAME, COMPANY_TAGLINE } from '@/lib/brand';

const FEATURES = [
  'Attendance, leaves & shifts',
  'Payslips, salary letters & certificates',
  'Reimbursements & company announcements',
];

// Faint solar-panel cell grid, fading upward
const PANEL_GRID: React.CSSProperties = {
  backgroundImage:
    'linear-gradient(#BCF5FD 1px, transparent 1px), linear-gradient(90deg, #BCF5FD 1px, transparent 1px)',
  backgroundSize: '46px 30px',
  maskImage: 'linear-gradient(to top, black 10%, transparent)',
  WebkitMaskImage: 'linear-gradient(to top, black 10%, transparent)',
};

const SUN_GLOW: React.CSSProperties = {
  background:
    'radial-gradient(circle, rgba(244,114,46,0.62) 0%, rgba(244,114,46,0.16) 45%, transparent 70%)',
};

/** Shown inside the card while the next auth screen's code loads; the panel stays put. */
function CardLoader() {
  return (
    <div
      className="flex min-h-[18rem] items-center justify-center"
      role="status"
      aria-label="Loading"
    >
      <Loader2 className="text-primary h-6 w-6 animate-spin" />
    </div>
  );
}

/**
 * Layout route for the sign-in, reset and first-login password screens: a navy brand
 * panel and the form card. Moving between those screens only swaps the card's content.
 * Phones: the panel is a hero at the top and the card overlaps its lower edge.
 * Desktop: the panel is the left column and the card sits centred on the right.
 */
export function AuthLayout() {
  const year = new Date().getFullYear();

  return (
    <div className="bg-background flex min-h-screen flex-col lg:flex-row">
      {/* ── Brand panel — always-dark navy, independent of app theme ── */}
      <aside className="bg-auth-panel text-auth-panel-fg relative overflow-hidden lg:sticky lg:top-0 lg:flex lg:h-screen lg:w-[46%] lg:max-w-[640px] lg:shrink-0 lg:flex-col">
        {/* Orange header bar, as on vayusolar.com */}
        <div className="bg-brand-orange absolute inset-x-0 top-0 h-1.5" />

        {/* Rising sun glow */}
        <div
          className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full lg:-right-32 lg:-top-32 lg:h-[30rem] lg:w-[30rem]"
          style={SUN_GLOW}
        />
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 h-3/5 opacity-[0.13]"
          style={PANEL_GRID}
        />

        <div className="relative z-10 flex flex-col px-6 pb-20 pt-8 sm:px-10 lg:h-full lg:px-12 lg:py-12">
          <BrandLockup onDark />

          <div className="mt-8 lg:my-auto lg:py-10">
            <p className="text-brand-orange text-[11px] font-semibold uppercase tracking-[0.22em]">
              {COMPANY_TAGLINE}
            </p>
            <h2 className="mt-3 text-[1.85rem] font-bold leading-[1.15] sm:text-4xl lg:text-[2.75rem] lg:leading-[1.1]">
              Powering the people
              <br className="hidden lg:block" /> behind{' '}
              <span className="text-brand-orange">spotless</span> solar.
            </h2>
            <p className="text-auth-panel-subtext mt-4 max-w-md text-sm leading-relaxed lg:text-[15px]">
              One place for attendance, leaves, payslips and claims, for everyone at {COMPANY_NAME}.
            </p>

            <ul className="mt-9 hidden gap-3 lg:grid">
              {FEATURES.map((f) => (
                <li
                  key={f}
                  className="bg-auth-panel-surface border-auth-panel-border flex items-center gap-3 rounded-xl border px-4 py-3"
                >
                  <span className="bg-brand-orange/20 flex h-7 w-7 shrink-0 items-center justify-center rounded-full">
                    <Check className="text-brand-orange h-3.5 w-3.5" strokeWidth={3} />
                  </span>
                  <span className="text-auth-panel-fg/90 text-sm">{f}</span>
                </li>
              ))}
            </ul>
          </div>

          <p className="text-auth-panel-muted hidden text-xs lg:block">
            © {year} {COMPANY_NAME}
          </p>
        </div>
      </aside>

      {/* ── Form side ── */}
      <div className="relative flex flex-1 flex-col">
        {/* Soft sky wash behind the card on desktop */}
        <div
          className="pointer-events-none absolute inset-0 hidden lg:block dark:opacity-20"
          style={{
            background:
              'radial-gradient(55% 55% at 72% 22%, rgba(188,245,253,0.45) 0%, transparent 70%)',
          }}
        />

        <main className="relative z-10 flex flex-1 flex-col items-center px-5 pb-10 sm:px-8 lg:justify-center lg:px-10 lg:py-14">
          <div className="-mt-12 w-full max-w-[27rem] lg:mt-0">
            <div className="bg-card shadow-brand-navy-deep/10 rounded-2xl border p-7 shadow-xl sm:p-9">
              <ErrorBoundary>
                <Suspense fallback={<CardLoader />}>
                  <Outlet />
                </Suspense>
              </ErrorBoundary>
            </div>
            <p className="text-muted-foreground mt-6 text-center text-xs">
              Trouble signing in? Ask your HR team for access.
            </p>
          </div>
        </main>

        <footer className="relative z-10 px-6 py-4">
          <p className="text-muted-foreground text-center text-xs">
            © {year} {COMPANY_NAME}. For internal use only.
          </p>
        </footer>
      </div>
    </div>
  );
}
